// web/src/admin/AdminClientDetail.tsx — /admin/clients/:slug (portal spec 22 §4d): one client, top to bottom:
// header (stage, days, next action, links) · demo (lead, Showcase, Bring back) · move over (legacy, X12) · go-live ticks +
// Start go-live · invites and logins · preferences · registry edit (stage with "force") · monitoring · monthly (runs,
// Run now, reports, the latest ideas with Yes / No thanks on the client's behalf, locks, requests, add a request) ·
// lifecycle tasks + messages · danger (site off/on, offboard) · audit. Every write is its owner spec's route.
import { useCallback, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminSiteDetail, RegistrySite, SiteStage, TickState } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Skeleton } from '../components/Skeleton';
import { Icon } from '../components/Icon';
import { PhotoPicker } from '../components/PhotoPicker';
import { toast } from '../components/Toast';
import { apiErrorText } from '../lib/errors';
import { StartGoLiveButton } from './StartGoLiveButton';
import { planLabel } from './BoardCard';
import { RunRow } from './AdminRuns';
import { Ideas } from './AdminReview';
import { Btn, Collapsible, Empty, Row, Rows, STAGE_LABEL, Section, StagePill, ago, fmtDate, inputCls, useAction } from './adminUi';

const STAGES: SiteStage[] = ['demo', 'sold', 'finishing', 'going_live', 'live', 'paused', 'offboarding', 'offboarded', 'archived', 'legacy'];
const PRE_LIVE: SiteStage[] = ['sold', 'finishing', 'going_live'];
const LIVE_TYPE: SiteStage[] = ['live', 'paused', 'offboarding', 'legacy'];
const TONE: Record<string, string> = { do: 'text-run', wait: 'text-muted', bad: 'text-danger', ok: 'text-success' };
const TICK: Record<TickState, { label: string; tone: string }> = { done: { label: 'Done', tone: 'text-success' }, todo: { label: 'Not yet', tone: 'text-warn' }, not_needed: { label: 'Not needed', tone: 'text-muted' } };

type Confirm = { title: string; body: string; label: string; destructive?: boolean; go: () => Promise<unknown> } | null;

export function AdminClientDetail() {
  const { slug = '' } = useParams();
  const q = useQuery({ queryKey: ['admin-site', slug], queryFn: () => api.adminSite(slug), retry: false });
  const [confirm, setConfirm] = useState<Confirm>(null);
  if (q.isLoading) return <Skeleton variant="card" count={3} />;
  if (q.isError || !q.data) return <Empty>{apiErrorText(q.error, 'No site with that slug.')} <Link className="underline" to="/admin">Back to the board</Link></Empty>;
  const d = q.data;
  const s = d.site;
  return (
    <>
      <Header d={d} />
      <div className="flex min-w-0 flex-col gap-4">
        {(s.stage === 'demo' || s.stage === 'archived') && <DemoSection d={d} />}
        {s.stage === 'legacy' && d.moveOver && <MoveOverSection d={d} ask={setConfirm} />}
        {PRE_LIVE.includes(s.stage) && <GoLiveSection d={d} />}
        <InvitesSection d={d} ask={setConfirm} />
        <PreferencesSection s={s} />
        <RegistrySection s={s} ask={setConfirm} />
        {(LIVE_TYPE.includes(s.stage) || d.monitor) && <MonitoringSection d={d} />}
        {LIVE_TYPE.includes(s.stage) && <MonthlySection d={d} ask={setConfirm} />}
        <LifecycleSection d={d} />
        <DangerSection d={d} ask={setConfirm} />
        <Section title="Audit (last 50)">
          {d.audit.length === 0 ? <p className="text-sm text-muted">Nothing yet.</p> : (
            <ul className="flex flex-col divide-y divide-hair text-sm">
              {d.audit.map((a) => (
                <li key={a.id} className="min-w-0 py-2">
                  <div className="flex flex-wrap justify-between gap-2"><span className="break-all font-mono text-xs text-ink">{a.action}</span><span className="text-xs text-muted">{fmtDate(a.createdAt, true)}</span></div>
                  <div className="break-all text-xs text-muted">{a.actor}{a.meta ? ` · ${JSON.stringify(a.meta).slice(0, 220)}` : ''}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
      <ConfirmDialog open={!!confirm} title={confirm?.title ?? ''} body={confirm?.body ?? ''} confirmLabel={confirm?.label ?? 'OK'} destructive={confirm?.destructive}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) void c.go(); }} onCancel={() => setConfirm(null)} />
    </>
  );
}

function ext(href: string | null | undefined, label: string) {
  if (!href) return null;
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm text-ink underline">{label}<Icon name="external" size={13} /></a>;
}

function Header({ d }: { d: AdminSiteDetail }) {
  const s = d.site;
  const cus = d.org?.stripeCustomerId;
  return (
    <div className="mb-4 min-w-0">
      <Link to="/admin" className="mb-2 inline-flex min-h-11 items-center gap-1.5 text-sm text-muted"><Icon name="arrow-left" size={15} />Clients</Link>
      <PageHeader title={s.name} subtitle={`${s.slug} · ${d.org?.name ?? ''}${d.isHouseOrg ? ' (house)' : ''}`} />
      <div className="-mt-3 flex flex-wrap items-center gap-2 text-sm">
        <StagePill stage={s.stage} /><span className="text-muted">{d.card.stageDays} days in this stage</span>
        <span className="text-muted">· {planLabel(d.card.plan)}</span>
      </div>
      {d.card.next && <p className={`mt-2 break-words font-medium ${TONE[d.card.next.tone]}`}>{d.card.next.label}</p>}
      {d.card.flags.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{d.card.flags.map((f) => <span key={f} className="rounded-full bg-danger/10 px-2 py-0.5 text-xs text-danger">{f}</span>)}</div>}
      <div className="mt-1 flex flex-wrap gap-x-4">
        {ext(s.liveHost ? `https://${s.liveHost}` : null, 'Live site')}
        {ext(s.previewUrl, 'Preview')}
        {ext(s.repoUrl, 'Repo')}
        {cus && <Link to={`/admin/customers/${encodeURIComponent(cus)}`} className="inline-flex min-h-11 items-center text-sm text-ink underline">Stripe customer</Link>}
      </div>
    </div>
  );
}

function DemoSection({ d }: { d: AdminSiteDetail }) {
  const s = d.site;
  const { busy, run } = useAction();
  return (
    <Section title="Demo">
      {d.lead ? (
        <Rows>
          <Row k="Lead">{d.lead.businessName}</Row>
          <Row k="Email"><a className="underline" href={`mailto:${d.lead.email}`}>{d.lead.email}</a></Row>
          {d.lead.phone && <Row k="Phone"><a className="underline" href={`tel:${d.lead.phone.replace(/[^0-9+]/g, '')}`}>{d.lead.phone}</a></Row>}
          <Row k="Asked">{fmtDate(d.lead.createdAt)} · {d.lead.status}</Row>
          <Row k="Biggest problem">{d.lead.biggestIssue}</Row>
        </Rows>
      ) : <p className="text-sm text-muted">{d.isHouseOrg ? 'Not attached to a lead (it sits in the house org). Attach it from Leads.' : 'No lead for this demo.'}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn aria-pressed={s.showcase} kind={s.showcase ? 'primary' : 'plain'} disabled={!!busy}
          onClick={() => void run('showcase', () => api.adminPatchSite(s.slug, { showcase: !s.showcase }), s.showcase ? 'Showcase off: it can be archived after 60 days unsold.' : 'Showcase on: it stays in your gallery.')}>
          {s.showcase ? 'Showcase: on' : 'Showcase: off'}</Btn>
        {s.stage === 'archived' && <Btn disabled={!!busy} onClick={() => void run('back', () => api.adminPatchSite(s.slug, { stage: 'demo' }), 'Back to demo.')}>Bring back</Btn>}
      </div>
      <p className="mt-2 text-xs text-muted">Showcase demos are never archived on their own.</p>
    </Section>
  );
}

const MOVE_LABEL: Record<string, string> = {
  repoUrl: 'Repo URL', liveHost: 'Live host', domain: 'Domain', amplifyAppId: 'Amplify app id', awsRegion: 'AWS region', cfZoneId: 'Cloudflare zone id',
  gscProperty: 'Search Console property', liveAt: 'Live since', runDay: 'Run day (1–28)', reportEmails: 'Report emails', updateMode: 'Update mode',
  planActive: 'Plan active (trialing or active)', monitoring: 'Monitoring on',
};
const PLACEHOLDER: Record<string, string> = {
  repoUrl: 'https://github.com/kc-hansen/site', liveHost: 'www.example.com', domain: 'example.com', amplifyAppId: 'd1a2b3c4', cfZoneId: '32 hex characters',
  gscProperty: 'sc-domain:example.com', runDay: '12', reportEmails: 'owner@example.com, office@example.com',
};
function moveValue(k: string, v: string): unknown {
  if (k === 'runDay') return Number(v);
  if (k === 'reportEmails') return v.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
  if (k === 'liveAt') return new Date(`${v}T12:00:00Z`).toISOString();
  return v.trim();
}

function MoveOverSection({ d, ask }: { d: AdminSiteDetail; ask: (c: Confirm) => void }) {
  const s = d.site;
  const { busy, run } = useAction();
  const missing = d.moveOver!.missing;
  const fields = missing.filter((k) => !['planActive', 'monitoring'].includes(k));
  const [v, setV] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((k) => [k, k === 'awsRegion' ? 'us-east-1' : k === 'updateMode' ? 'show_me' : ''])));
  const filled = fields.every((k) => (v[k] ?? '').trim() !== '');
  const blocked = missing.includes('planActive');
  const patch = () => Object.fromEntries(fields.map((k) => [k, moveValue(k, v[k] ?? '')]));
  return (
    <Section title="Move over to live (legacy)">
      <p className="text-sm text-muted">A legacy site needs these before its monthly updates can run. Monitoring switches on when you press Move to live.</p>
      <ul className="mt-3 flex flex-col gap-3">
        {Object.keys(MOVE_LABEL).map((k) => {
          const isMissing = missing.includes(k);
          return (
            <li key={k} className="min-w-0 text-sm">
              <div className="flex items-center gap-2"><Icon name={isMissing ? 'alert' : 'check'} size={14} className={isMissing ? 'text-warn' : 'text-success'} /><span className="text-ink">{MOVE_LABEL[k]}</span></div>
              {isMissing && fields.includes(k) && (
                k === 'awsRegion' || k === 'updateMode' ? (
                  <select aria-label={MOVE_LABEL[k]} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={`${inputCls} mt-1`}>
                    {(k === 'awsRegion' ? ['us-east-1', 'us-west-2'] : ['show_me', 'auto']).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input aria-label={MOVE_LABEL[k]} type={k === 'liveAt' ? 'date' : k === 'runDay' ? 'number' : 'text'} min={k === 'runDay' ? 1 : undefined} max={k === 'runDay' ? 28 : undefined}
                    value={v[k] ?? ''} placeholder={PLACEHOLDER[k]} autoCapitalize="none" spellCheck={false} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={`${inputCls} mt-1`} />
                )
              )}
              {isMissing && k === 'planActive' && <p className="mt-1 text-xs text-muted">The client needs a Website care plan (trialing or active).{d.org?.stripeCustomerId ? <> See <Link className="underline" to={`/admin/customers/${encodeURIComponent(d.org.stripeCustomerId)}`}>Billing</Link>.</> : ''}</p>}
            </li>
          );
        })}
      </ul>
      <div className="mt-4">
        <Btn kind="primary" disabled={!filled || blocked || !!busy} onClick={() => ask({ title: `Move ${s.name} to live?`, body: 'The fields are saved, the stage becomes Live, monitoring switches on and the first monthly run is scheduled at least 21 days out.',
          label: 'Move to live', go: () => run('move', () => api.adminMoveOver(s.slug, patch()), 'Moved to live.') })}>Move to live</Btn>
        {blocked && <p className="mt-2 text-xs text-warn">Waiting for an active plan.</p>}
      </div>
    </Section>
  );
}

function GoLiveSection({ d }: { d: AdminSiteDetail }) {
  const o = d.onboarding;
  const qc = useQueryClient();
  const [checking, setChecking] = useState(false);
  const recheck = async () => {
    setChecking(true);
    try { await api.adminOnboarding(d.site.slug, true); void qc.invalidateQueries({ queryKey: ['admin-site', d.site.slug] }); }
    catch (e) { toast({ kind: 'error', message: apiErrorText(e, 'Couldn’t check right now.') }); }
    finally { setChecking(false); }
  };
  return (
    <Section title="Go-live">
      {!o ? <p className="text-sm text-muted">The checklist can't be read right now.</p> : (
        <>
          <Rows>
            {([['Account', o.account], ['Plan', o.plan], ['Payments', o.stripe], ['Preferences', o.preferences], ['Nameservers', o.nameservers]] as const).map(([k, t]) => (
              <Row key={k} k={k}><span className={TICK[t].tone}>{TICK[t].label}</span></Row>
            ))}
          </Rows>
          <p className="mt-2 text-xs text-muted">Payments and preferences never block go-live. Checked {fmtDate(o.checkedAt, true)}.</p>
          {o.nameserverInfo && (
            <div className="mt-3 rounded-md border border-hair p-3 text-sm">
              <p className="text-muted">For the call: set these nameservers{o.nameserverInfo.registrar ? ` at ${o.nameserverInfo.registrar}` : ''}</p>
              {o.nameserverInfo.nameServers.map((ns) => <div key={ns} className="mt-1 break-all font-mono text-ink">{ns}</div>)}
              {o.nameserverInfo.currentNameServers.length > 0 && <p className="mt-2 break-words text-xs text-muted">Now: {o.nameserverInfo.currentNameServers.join(', ')}</p>}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <StartGoLiveButton slug={d.site.slug} ready={o.ready} stage={d.site.stage} />
            {o.nameservers !== 'not_needed' && <Btn icon="refresh" disabled={checking} onClick={() => void recheck()}>{checking ? 'Checking…' : 'Check again'}</Btn>}
          </div>
        </>
      )}
    </Section>
  );
}

function InvitesSection({ d, ask }: { d: AdminSiteDetail; ask: (c: Confirm) => void }) {
  const s = d.site;
  const inv = useQuery({ queryKey: ['admin-invites', s.slug], queryFn: () => api.adminInvites(s.slug) });
  const { busy, run } = useAction();
  const [email, setEmail] = useState('');
  const [biz, setBiz] = useState(s.name);
  const [url, setUrl] = useState<string | null>(null);
  const create = async () => {
    const r = await run('invite', () => api.adminInvite(s.slug, email.trim(), biz.trim() || undefined), 'Invite made. Copy the link now: it is shown once.');
    if (r) { setUrl(r.url); setEmail(''); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url ?? ''); toast({ kind: 'success', message: 'Copied.' }); }
    catch { toast({ kind: 'error', message: 'Couldn’t copy: select the link and copy it by hand.' }); }
  };
  return (
    <Section title="Invites and logins">
      {s.stage !== 'offboarded' && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <input aria-label="Invite email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" autoCapitalize="none" className={inputCls} />
          <input aria-label="Business name" value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Business name" className={inputCls} />
          <Btn kind="primary" disabled={!/^\S+@\S+\.\S+$/.test(email) || !!busy} onClick={() => void create()}>Invite</Btn>
        </div>
      )}
      {url && (
        <div className="mt-3 rounded-md border border-run p-3 text-sm">
          <p className="text-muted">Shown once. Send it to the client:</p>
          <p className="mt-1 break-all font-mono text-ink">{url}</p>
          <div className="mt-2 flex gap-2"><Btn icon="file" onClick={() => void copy()}>Copy</Btn><Btn kind="quiet" onClick={() => setUrl(null)}>Done</Btn></div>
        </div>
      )}
      {(inv.data ?? []).length > 0 && (
        <ul className="mt-3 flex flex-col divide-y divide-hair text-sm">
          {(inv.data ?? []).map((i) => {
            const open = !i.acceptedAt && !i.revokedAt && Date.parse(i.expiresAt) > Date.now();
            return (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="min-w-0"><div className="break-all text-ink">{i.email}</div>
                  <div className="text-xs text-muted">{i.acceptedAt ? `accepted ${fmtDate(i.acceptedAt)}` : i.revokedAt ? `revoked ${fmtDate(i.revokedAt)}` : open ? `open until ${fmtDate(i.expiresAt)}` : 'expired'} · by {i.createdBy}</div></div>
                {open && <Btn kind="danger" disabled={!!busy} onClick={() => void run('revoke', () => api.adminRevokeInvite(i.id), 'Invite revoked.')}>Revoke</Btn>}
              </li>
            );
          })}
        </ul>
      )}
      <h3 className="mt-4 text-sm text-muted">Logins in this workspace</h3>
      {d.isHouseOrg ? <p className="text-sm text-muted">The house org has no client logins.</p> : d.users.length === 0 ? <p className="text-sm text-muted">Nobody has signed in yet.</p> : (
        <ul className="mt-1 flex flex-col divide-y divide-hair text-sm">
          {d.users.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0"><div className="break-all text-ink">{u.email}</div><div className="text-xs text-muted">{u.role} · last sign-in {u.lastLogin ? ago(u.lastLogin) : 'never'}</div></div>
              <Btn kind="danger" disabled={!!busy || !d.org} onClick={() => ask({ title: `Remove ${u.email}?`, body: 'They leave this workspace (their login stays). The last owner can’t be removed.', label: 'Remove', destructive: true,
                go: () => run('remove', () => api.adminRemoveLogin(d.org!.id, u.id), (r) => `${r.removed} removed.`) })}>Remove</Btn>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PreferencesSection({ s }: { s: RegistrySite }) {
  const { busy, run } = useAction();
  const [emails, setEmails] = useState(s.reportEmails.join(', '));
  const [mode, setMode] = useState(s.updateMode);
  const [gbp, setGbp] = useState<boolean | null>(s.gbpManaged);
  const list = emails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
  const bad = list.filter((e) => !/^\S+@\S+\.\S+$/.test(e));
  const patch: Record<string, unknown> = {};
  if (list.join(',') !== s.reportEmails.join(',')) patch.reportEmails = list;
  if (mode !== s.updateMode) patch.updateMode = mode;
  if (gbp !== s.gbpManaged && gbp !== null) patch.gbpManaged = gbp;
  const choice = (on: boolean, label: string, onClick: () => void) => (
    <button type="button" aria-pressed={on} onClick={onClick} className={`min-h-11 rounded-md border px-3 text-sm ${on ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>{label}</button>
  );
  return (
    <Section title="Preferences (the client's; fix them on a call)">
      <label className="flex flex-col gap-1 text-sm"><span className="text-muted">Report emails</span>
        <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="owner@example.com" autoCapitalize="none" className={inputCls} /></label>
      {bad.length > 0 && <p className="mt-1 text-xs text-danger">Not an email: {bad.join(', ')}</p>}
      <div className="mt-3 text-sm text-muted">Updates</div>
      <div className="mt-1 flex flex-wrap gap-2">{choice(mode === 'show_me', 'Show me first', () => setMode('show_me'))}{choice(mode === 'auto', 'Update automatically', () => setMode('auto'))}</div>
      <div className="mt-3 text-sm text-muted">Manage their Google Business Profile{s.gbpManaged === null ? ' (not asked yet)' : ''}</div>
      <div className="mt-1 flex flex-wrap gap-2">{choice(gbp === true, 'Yes', () => setGbp(true))}{choice(gbp === false, 'No', () => setGbp(false))}</div>
      <div className="mt-3"><Btn kind="primary" disabled={!Object.keys(patch).length || bad.length > 0 || list.length > 5 || !!busy}
        onClick={() => void run('prefs', () => api.adminPatchSite(s.slug, patch), 'Preferences saved.')}>Save preferences</Btn></div>
    </Section>
  );
}

type RegKey = 'name' | 'domain' | 'liveHost' | 'previewUrl' | 'repoUrl' | 'runDay' | 'costCapUsd' | 'kcReviewRunsLeft' | 'nextRunAt' | 'liveAt';
const REG_FIELDS: { k: RegKey; label: string; type?: string }[] = [
  { k: 'name', label: 'Name' }, { k: 'domain', label: 'Domain' }, { k: 'liveHost', label: 'Live host' }, { k: 'previewUrl', label: 'Preview URL', type: 'url' },
  { k: 'repoUrl', label: 'Repo URL', type: 'url' }, { k: 'runDay', label: 'Run day (1–28)', type: 'number' }, { k: 'costCapUsd', label: 'Cost cap per run (USD, admin only)' },
  { k: 'kcReviewRunsLeft', label: 'Reviews left (admin only)', type: 'number' }, { k: 'nextRunAt', label: 'Next run (admin only)', type: 'datetime-local' },
  { k: 'liveAt', label: 'Live since', type: 'datetime-local' },
];
const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '');
const fromField = (k: RegKey, v: string): unknown => (k === 'runDay' || k === 'kcReviewRunsLeft' ? Number(v) : k === 'nextRunAt' || k === 'liveAt' ? new Date(v).toISOString() : v.trim());
const current = (s: RegistrySite, k: RegKey): string => {
  const v = s[k];
  if (v === null || v === undefined) return '';
  return k === 'nextRunAt' || k === 'liveAt' ? toLocal(String(v)) : String(v);
};

function RegistrySection({ s, ask }: { s: RegistrySite; ask: (c: Confirm) => void }) {
  const { busy, run } = useAction();
  const initial = useCallback(() => Object.fromEntries(REG_FIELDS.map((f) => [f.k, current(s, f.k)])) as Record<RegKey, string>, [s]);
  const [v, setV] = useState<Record<RegKey, string>>(initial);
  const [payments, setPayments] = useState(s.takesPayments);
  const [showcase, setShowcase] = useState(s.showcase);
  const [stage, setStage] = useState<SiteStage>(s.stage);
  const [reason, setReason] = useState<'payment_failed' | 'client_request' | 'admin'>('admin');
  const [force, setForce] = useState(false);
  const patch: Record<string, unknown> = {};
  for (const f of REG_FIELDS) if (v[f.k] !== current(s, f.k) && v[f.k].trim() !== '') patch[f.k] = fromField(f.k, v[f.k]);
  if (payments !== s.takesPayments) patch.takesPayments = payments;
  if (showcase !== s.showcase) patch.showcase = showcase;
  if (stage !== s.stage) { patch.stage = stage; if (stage === 'paused') patch.pausedReason = reason; }
  const cleared = REG_FIELDS.filter((f) => v[f.k].trim() === '' && current(s, f.k) !== '').map((f) => f.label);
  const save = () => {
    const go = () => run('registry', () => api.adminPatchSite(s.slug, patch, patch.stage ? force : undefined), 'Saved to the registry.');
    if (patch.stage) ask({ title: `Change the stage: ${STAGE_LABEL[s.stage]} → ${STAGE_LABEL[stage]}?`, body: force ? 'Forced: this skips the stage rules. Stages normally move by real events (payment, Start go-live, the robot).' : 'Only moves the stage rules allow will work; tick “force” to skip them.', label: 'Change stage', destructive: force, go });
    else void go();
  };
  return (
    <Section title="Registry">
      <div className="grid gap-3 sm:grid-cols-2">
        {REG_FIELDS.map((f) => (
          <label key={f.k} className="flex min-w-0 flex-col gap-1 text-sm"><span className="text-muted">{f.label}</span>
            <input type={f.type ?? 'text'} value={v[f.k]} onChange={(e) => setV({ ...v, [f.k]: e.target.value })} autoCapitalize="none" spellCheck={false} className={inputCls} /></label>
        ))}
        <label className="flex min-w-0 flex-col gap-1 text-sm"><span className="text-muted">Stage</span>
          <select value={stage} onChange={(e) => setStage(e.target.value as SiteStage)} className={inputCls}>
            {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABEL[st]} ({st})</option>)}
          </select></label>
        {stage === 'paused' && stage !== s.stage && (
          <label className="flex min-w-0 flex-col gap-1 text-sm"><span className="text-muted">Why paused</span>
            <select value={reason} onChange={(e) => setReason(e.target.value as typeof reason)} className={inputCls}>
              <option value="admin">admin</option><option value="client_request">client asked</option><option value="payment_failed">payment failed</option>
            </select></label>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5">
        <label className="flex min-h-11 items-center gap-2 text-sm text-ink"><input type="checkbox" className="size-5" checked={payments} onChange={(e) => setPayments(e.target.checked)} />Takes payments online</label>
        <label className="flex min-h-11 items-center gap-2 text-sm text-ink"><input type="checkbox" className="size-5" checked={showcase} onChange={(e) => setShowcase(e.target.checked)} />Showcase</label>
        {stage !== s.stage && <label className="flex min-h-11 items-center gap-2 text-sm text-danger"><input type="checkbox" className="size-5" checked={force} onChange={(e) => setForce(e.target.checked)} />Force the stage</label>}
      </div>
      {cleared.length > 0 && <p className="mt-1 text-xs text-warn">Empty fields aren't cleared here ({cleared.join(', ')}).</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Btn kind="primary" disabled={!Object.keys(patch).length || !!busy} onClick={save}>Save</Btn>
        <Btn kind="quiet" onClick={() => { setV(initial()); setPayments(s.takesPayments); setShowcase(s.showcase); setStage(s.stage); setForce(false); }}>Reset</Btn>
      </div>
    </Section>
  );
}

function MonitoringSection({ d }: { d: AdminSiteDetail }) {
  const m = d.monitor;
  const { busy, run } = useAction();
  const [host, setHost] = useState(m?.host || d.site.liveHost || '');
  const dot = !m || !m.enabled ? 'border border-hair' : m.status === 'up' ? 'bg-success' : m.status === 'down' ? 'bg-danger' : 'bg-muted';
  const certDays = m?.certExpiresAt ? Math.floor((Date.parse(m.certExpiresAt) - Date.now()) / 86_400_000) : null;
  return (
    <Section title="Monitoring">
      <Rows>
        <Row k="Status"><span className="inline-flex items-center gap-1.5"><span className={`inline-block size-2 rounded-full ${dot}`} />{!m ? 'not set up' : !m.enabled ? 'off' : m.status}</span></Row>
        {m && <Row k="Since">{fmtDate(m.since, true)}</Row>}
        {m && <Row k="Last OK">{fmtDate(m.lastOkAt, true)}</Row>}
        {m && <Row k="Last check">{m.lastCheckAt ? ago(m.lastCheckAt) : '—'}</Row>}
        {m && <Row k="Certificate">{m.certError ? <span className="text-danger">{m.certError}</span> : certDays !== null ? <span className={certDays < 14 ? 'text-danger' : ''}>{fmtDate(m.certExpiresAt)} ({certDays} days)</span> : '—'}</Row>}
      </Rows>
      <label className="mt-3 flex flex-col gap-1 text-sm"><span className="text-muted">Host</span>
        <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="www.example.com" autoCapitalize="none" spellCheck={false} className={inputCls} /></label>
      <div className="mt-3 flex flex-wrap gap-2">
        {m?.enabled
          ? <Btn disabled={!!busy} onClick={() => void run('mon', () => api.adminMonitorToggle(d.site.slug, false), 'Monitoring off.')}>Switch off</Btn>
          : <Btn kind="primary" disabled={!host.trim() || !!busy} onClick={() => void run('mon', () => api.adminMonitorToggle(d.site.slug, true, host.trim()), 'Monitoring on.')}>Switch on</Btn>}
        {m?.enabled && host.trim() && host.trim() !== m.host && <Btn disabled={!!busy} onClick={() => void run('mon', () => api.adminMonitorToggle(d.site.slug, true, host.trim()), 'Host changed.')}>Change host</Btn>}
        {m && m.host && <Btn icon="refresh" disabled={!!busy} onClick={() => void run('check', () => api.adminMonitorCheck(d.site.slug), (r) => `Checked: ${r.status}.`, 'Couldn’t check.')}>{busy === 'check' ? 'Checking…' : 'Check now'}</Btn>}
      </div>
    </Section>
  );
}

function MonthlySection({ d, ask }: { d: AdminSiteDetail; ask: (c: Confirm) => void }) {
  const s = d.site;
  const { busy, run } = useAction();
  const [postReport, setPostReport] = useState(true);
  const waiting = d.requests.some((r) => r.status === 'publish_waiting');
  const decide = (itemId: string, decision: 'approve' | 'decline') => {
    if (!d.proposal) return;
    void run('decide', () => api.adminDecideOnBehalf(d.proposal!.id, itemId, decision), decision === 'approve' ? 'Answered yes for the client.' : 'Answered no thanks for the client.');
  };
  return (
    <Section title="Monthly">
      <div className="flex flex-wrap items-center gap-3">
        <Btn kind="primary" disabled={!!busy || (s.stage !== 'live' && s.stage !== 'legacy')} onClick={() => void run('runnow', () => api.adminRunUpdate(s.slug, postReport), 'Run queued.')}>Run now</Btn>
        <label className="flex min-h-11 items-center gap-2 text-sm text-ink"><input type="checkbox" className="size-5" checked={postReport} onChange={(e) => setPostReport(e.target.checked)} />Post the month's report</label>
      </div>
      <p className="mt-1 text-xs text-muted">Next run {fmtDate(s.nextRunAt, true)} (run day {s.runDay ?? '—'}) · {s.kcReviewRunsLeft} reviews left. An out-of-cycle fix is a request (below).</p>

      <h3 className="mt-4 text-sm text-muted">Runs (last 12)</h3>
      {d.runs.length === 0 ? <p className="text-sm text-muted">No runs yet.</p> : <div className="mt-1 flex flex-col gap-2">{d.runs.map((r) => <RunRow key={r.id} r={r} />)}</div>}

      <h3 className="mt-4 text-sm text-muted">Reports</h3>
      {s.reportEmails.length === 0 && <p className="text-xs text-warn">No report email set: published reports show on the dashboard only.</p>}
      {d.reports.length === 0 ? <p className="text-sm text-muted">No reports yet.</p> : (
        <ul className="mt-1 flex flex-col divide-y divide-hair text-sm">
          {d.reports.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div><div className="text-ink">{r.period} · {r.status}</div><div className="text-xs text-muted">{r.publishedAt ? `published ${fmtDate(r.publishedAt)}` : 'draft'}{r.emailedAt ? ` · note queued ${fmtDate(r.emailedAt)}` : ''}</div></div>
              {r.status === 'draft'
                ? <Btn disabled={!!busy} onClick={() => void run('pub', () => api.adminPublishReport(r.id), (x) => (x.emailed ? 'Published. The monthly note waits in the Outbox.' : 'Published.'))}>Publish</Btn>
                : <Btn disabled={!!busy} onClick={() => void run('unpub', () => api.adminUnpublishReport(r.id), 'Unpublished: the client no longer sees it.')}>Unpublish</Btn>}
            </li>
          ))}
        </ul>
      )}

      <h3 className="mt-4 text-sm text-muted">Latest ideas</h3>
      {!d.proposal ? <p className="text-sm text-muted">None yet.</p> : (
        <>
          <p className="text-xs text-muted">{d.proposal.period} · {d.proposal.status}{d.proposal.autoYesOn ? ` · no answer by ${fmtDate(d.proposal.autoYesOn)} means yes` : ''}</p>
          <Ideas p={d.proposal} onBehalf={decide} />
        </>
      )}

      <h3 className="mt-4 text-sm text-muted">Locks</h3>
      <Rows>
        <Row k="Request lock (dev)">{s.busyRef ? `${s.busyRef} since ${fmtDate(s.busySince, true)}` : 'free'}</Row>
        <Row k="Publish lock (main)">{s.publishRef ? `${s.publishRef} since ${fmtDate(s.publishSince, true)}` : 'free'}</Row>
      </Rows>
      <div className="mt-2 flex flex-wrap gap-2">
        {s.busyRef && <Btn kind="danger" disabled={!!busy} onClick={() => ask({ title: 'Release the request lock?', body: `${s.busyRef} loses its turn on dev; the next waiting request starts.`, label: 'Release request lock', destructive: true,
          go: () => run('rl', () => api.adminReleaseRequestLock(s.slug), (r) => (r.released ? 'Request lock released.' : 'It was already free.')) })}>Release request lock</Btn>}
        {s.publishRef && waiting && <Btn kind="danger" disabled={!!busy} onClick={() => ask({ title: 'Checked main on GitHub?', body: `Release the publish lock held by ${s.publishRef}: the waiting request goes out next.`, label: 'Release publish lock', destructive: true,
          go: () => run('pl', () => api.adminReleasePublishLock(s.slug), (r) => (r.released ? 'Publish lock released.' : 'It was already free.')) })}>Release publish lock</Btn>}
      </div>

      <h3 className="mt-4 text-sm text-muted">Requests</h3>
      {d.requests.length === 0 ? <p className="text-sm text-muted">No requests.</p> : (
        <ul className="mt-1 flex flex-col divide-y divide-hair text-sm">
          {d.requests.map((r) => <li key={r.id} className="py-2"><div className="break-words text-ink">{r.title}</div><div className="text-xs text-muted">{r.status} · {r.kind} · {r.source ?? 'dashboard'} · {ago(r.createdAt)}</div></li>)}
        </ul>
      )}
      {(s.stage === 'live' || s.stage === 'legacy') && <AddRequest slug={s.slug} />}
    </Section>
  );
}

function AddRequest({ slug }: { slug: string }) {
  const { busy, run } = useAction();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [source, setSource] = useState<'email' | 'admin'>('email');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [key, setKey] = useState(0);
  const onPhotos = useCallback((urls: string[], up: boolean) => { setPhotos(urls); setUploading(up); }, []);
  const sas = useCallback((f: string, c: string) => api.adminUploadSas(slug, f, c), [slug]);
  const send = async () => {
    const full = [body.trim(), ...photos.map((u) => `Uploaded image: ${u}`)].join('\n');
    const r = await run('req', () => api.adminCreateRequest(slug, { title: title.trim(), body: full, source }), (x) => `Request stored (${x.status}).`);
    if (r) { setTitle(''); setBody(''); setPhotos([]); setKey((k) => k + 1); }
  };
  return (
    <div className="mt-4 rounded-md border border-hair p-3">
      <h3 className="text-sm text-ink">Add a request for the client</h3>
      <p className="text-xs text-muted">For a change they emailed or phoned in. It runs like any request from their dashboard.</p>
      <div className="mt-2 flex flex-col gap-2">
        <input aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What they asked for, in a line" className={inputCls} />
        <textarea aria-label="Details" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="The details, in their words" className={`${inputCls} py-2`} />
        <div className="flex flex-wrap gap-2">
          {(['email', 'admin'] as const).map((k) => <button key={k} type="button" aria-pressed={source === k} onClick={() => setSource(k)}
            className={`min-h-11 rounded-md border px-3 text-sm ${source === k ? 'border-run bg-run/10 text-ink' : 'border-hair text-muted'}`}>{k === 'email' ? 'They emailed it' : 'My own'}</button>)}
        </div>
        <PhotoPicker key={key} onChange={onPhotos} sas={sas} />
        <div><Btn kind="primary" disabled={title.trim().length < 3 || body.trim().length < 3 || uploading || !!busy} onClick={() => void send()}>Add the request</Btn></div>
      </div>
    </div>
  );
}

function LifecycleSection({ d }: { d: AdminSiteDetail }) {
  return (
    <Section title="Lifecycle and messages" action={<span className="flex gap-3 text-sm"><Link className="inline-flex min-h-11 items-center underline" to="/admin/lifecycle">Lifecycle</Link><Link className="inline-flex min-h-11 items-center underline" to="/admin/outbox">Outbox</Link></span>}>
      <Collapsible title="Tasks" count={d.tasks.length} defaultOpen={d.tasks.some((t) => ['open', 'running', 'failed'].includes(t.status))}>
        {d.tasks.length === 0 ? <p className="text-sm text-muted">None.</p> : (
          <ul className="flex flex-col divide-y divide-hair text-sm">
            {d.tasks.map((t) => <li key={t.id} className="py-2"><span className="text-ink">{t.kind}</span> <span className="text-muted">· {t.status} · {ago(t.createdAt)} · {t.createdBy}</span>
              {t.status === 'failed' && t.payload?.error ? <div className="break-words text-xs text-danger">{String(t.payload.error)}</div> : null}</li>)}
          </ul>
        )}
      </Collapsible>
      <Collapsible title="Messages" count={d.messages.length} defaultOpen={d.messages.some((m) => m.status === 'draft' || m.status === 'failed')}>
        {d.messages.length === 0 ? <p className="text-sm text-muted">None.</p> : (
          <ul className="flex flex-col divide-y divide-hair text-sm">
            {d.messages.map((m) => <li key={m.id} className="min-w-0 py-2"><div className="break-words text-ink">{m.subject ?? m.template}</div><div className="break-all text-xs text-muted">{m.template} · {m.status} · {m.toEmails.join(', ')} · {ago(m.createdAt)}</div></li>)}
          </ul>
        )}
      </Collapsible>
    </Section>
  );
}

function DangerSection({ d, ask }: { d: AdminSiteDetail; ask: (c: Confirm) => void }) {
  const s = d.site;
  const { busy, run } = useAction();
  const [cancelPlan, setCancelPlan] = useState(true);
  const canOffboard = ['live', 'paused', 'legacy', 'offboarding'].includes(s.stage);
  const row = (children: ReactNode) => <div className="flex flex-wrap gap-2">{children}</div>;
  return (
    <Section title="Danger">
      <p className="text-sm text-muted">{d.switchedOff ? `Switched off since ${fmtDate(d.switchedOff.offAt, true)} (${d.switchedOff.reason}).` : 'The site is on.'} Switching needs SITE_OFF_ENABLED and a Cloudflare zone.</p>
      {row(<>
        <Btn kind="danger" disabled={!!busy} onClick={() => ask({ title: `Turn ${s.name} off?`, body: 'Visitors see the “taking a short break” page. Email (MX) keeps working.', label: 'Turn site off', destructive: true, go: () => run('off', () => api.adminSiteOff(s.slug), 'Site switched off.') })}>Turn site off</Btn>
        <Btn disabled={!!busy} onClick={() => ask({ title: `Turn ${s.name} back on?`, body: 'Visitors see the site again right away.', label: 'Turn site on', go: () => run('on', () => api.adminSiteOn(s.slug), 'Site switched back on.') })}>Turn site on</Btn>
      </>)}
      {canOffboard && (
        <div className="mt-4 rounded-md border border-danger/40 p-3">
          <label className="flex min-h-11 items-center gap-2 text-sm text-ink"><input type="checkbox" className="size-5" checked={cancelPlan} onChange={(e) => setCancelPlan(e.target.checked)} />Cancel their plan in Stripe too</label>
          <Btn kind="danger" disabled={!!busy || s.stage === 'offboarding'} onClick={() => ask({ title: `Offboard ${s.name}?`, body: 'The stage becomes Ending, the plan is canceled (if ticked), and the go-live brain gets the site-off and offboard tasks. Nothing is deleted.', label: 'Offboard', destructive: true,
            go: () => run('offboard', () => api.adminOffboard(s.slug, cancelPlan), 'Offboarding started.') })}>{s.stage === 'offboarding' ? 'Offboarding' : 'Offboard'}</Btn>
        </div>
      )}
    </Section>
  );
}
